/* @flow */

import { either } from 'ramda';
import { hasURLProtocol } from '@braindate/util/lib/url';
import { getEventLoginURL } from '@braindate/domain/lib/event/util';
import { isMembershipOnboardingCompleted } from '@braindate/domain/lib/membership/util';

import {
  homeRoute,
  signupRoute,
  loginRoute,
} from 'src/shared/app/base/route/setting/routeSettings';
import {
  getInitialUrl,
  getAuthenticationToken,
} from 'src/shared/app/authentication/selector/base/appAuthenticationBaseSelectors';
import { getKlikProfileURL } from 'plugin/klik/util/klikUtils';
import { isOnboardingCompletedOnDevice } from 'src/shared/app/onboarding/selector/uiOnboardingSelectors';
import { isOptinDone } from 'src/shared/app/permission/util/permissionUtils';
import { isSignupCompleted } from 'src/shared/app/signup/selector/uiSignupSelector';
import { getEventSafely } from 'src/shared/domain/event/selector/eventSelectors';
import { getMembership } from 'src/shared/domain/membership/selector/membershipSelectors';
import { getSelf } from 'src/shared/domain/user/selector/userSelectors';

import type { Location } from 'react-router';
import type { State } from 'src/shared/core/type/reduxTypes';
import { getPathWithSearchString } from 'src/shared/app/authentication/util/urlUtils';

const debug = require('debug')('redirectUtils');

export function standardAuthenticatedPageRedirect(
  state: State,
  config: Object,
  location: Location,
): ?string {
  return either(
    either(redirectToLoginUnlessAuthenticated, redirectToSignupUnlessCompleted),
    redirecToHomeUnlessOnboardingAndOptinCompleted,
  )(state, config, location);
}

export function redirectToLoginUnlessAuthenticated(
  state: State,
  _config: Object,
  location: Location,
): ?string {
  if (!getAuthenticationToken(state)) {
    return getPathWithSearchString(location, loginRoute);
  }
}

export function redirectToSignupUnlessCompleted(
  state: State,
  { isSignupEnabled }: Object,
  location: Location,
): ?string {
  if (isSignupEnabled) {
    const user = getSelf(state);

    if (user && !isSignupCompleted(state, user)) {
      return getPathWithSearchString(location, signupRoute);
    }
  }
}

export function redirecToHomeUnlessOnboardingAndOptinCompleted(
  state: State,
  { areEmailNotificationsEnabled }: Object,
  location: Location,
): ?string {
  const membership = getMembership(state);

  if (
    membership &&
    (!isMembershipOnboardingCompleted(membership) ||
      !isOptinDone(membership, areEmailNotificationsEnabled))
  ) {
    debug('redirecToHomeUnlessOnboardingAndOptinCompleted');
    return getPathWithSearchString(location, homeRoute);
  }
}

export function redirectWhenRedirectAndAuthenticated(state: State): ?string {
  const initialURL = getInitialUrl(state);

  if (initialURL && getAuthenticationToken(state)) {
    return initialURL;
  }
}

export function redirectToLoginIfOnboardingCompletedOnDevice(
  state: State,
  _config: Object,
  location: Location,
): ?string {
  if (!getAuthenticationToken(state) && isOnboardingCompletedOnDevice(state)) {
    return getPathWithSearchString(location, loginRoute);
  }
}

export function redirectToHomeWhenSignupDisabled(
  state: State,
  { isSignupEnabled }: Object,
  location: Location,
): ?string {
  if (!isSignupEnabled) {
    debug('redirectToHomeWhenSignupDisabled');
    return getPathWithSearchString(location, homeRoute);
  }
}

export function redirectToHomeWhenSignupCompleted(
  state: State,
  _config: Object,
  location: Location,
): ?string {
  const user = getSelf(state);

  if (user && isSignupCompleted(state, user)) {
    debug('redirectToHomeWhenSignupCompleted');
    return getPathWithSearchString(location, homeRoute);
  }
}

export function redirectToHomeWhenAuthenticated(
  state: State,
  _config: Object,
  location: Location,
): ?string {
  if (getAuthenticationToken(state)) {
    debug('redirectToHomeWhenAuthenticated');
    return getPathWithSearchString(location, homeRoute);
  }
}

export function redirectToLoginURL(state: State): ?string {
  const loginURL = getEventLoginURL(getEventSafely(state));

  if (loginURL && hasURLProtocol(loginURL)) {
    return loginURL;
  }
}

export function integrationCustomRedirect(state: State): ?string {
  return getKlikProfileURL(getEventSafely(state));
}

export function standardUnauthenticatedPageRedirect(
  state: State,
  config: Object,
  location: Location,
): ?string {
  debug('standardUnauthenticatedPageRedirect');
  return redirectToHomeWhenAuthenticated(state, config, location);
}
